/** Host OS — Customers. The team's internal CRM: everyone the business has
 *  interacted with, plus manual contacts, with status, tags, notes & export. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Contact, Loader2, Search, Download, UserPlus, Users, Upload, FileText, MoreHorizontal, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import AddCustomerDialog from "@/components/host/AddCustomerDialog";
import { HostPageHeader, KpiCard, formatMoneyMap } from "@/components/host/hostUi";
import { StatusPill, STATUS_FILTERS, STATUS_OPTIONS } from "@/components/host/crmUi";

const initials = (n) => (!n ? "?" : n.split(" ").map((x) => x[0]).join("").toUpperCase().slice(0, 2));

const lastInteraction = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const HostCustomersPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", city: "", status: "lead" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/crm/customers`, { headers });
      if (res.ok) setData(await res.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary;

  const rows = useMemo(() => {
    let all = data?.customers || [];
    if (statusFilter !== "all") all = all.filter((r) => r.status === statusFilter);
    if (tagFilter) all = all.filter((r) => (r.tags || []).includes(tagFilter));
    const term = q.trim().toLowerCase();
    if (term) all = all.filter((r) =>
      (r.name || "").toLowerCase().includes(term) ||
      (r.email || "").toLowerCase().includes(term) ||
      (r.phone || "").toLowerCase().includes(term) ||
      (r.city || "").toLowerCase().includes(term));
    return all;
  }, [data, q, statusFilter, tagFilter]);

  const exportCsv = async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/crm/export`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "customers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const downloadTemplate = () => {
    const csv = "Name,Email,Phone,City,Status,Tags\nJane Doe,jane@example.com,+49 170 000000,Berlin,lead,\"Pilates, VIP\"\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "customers-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a CSV file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const headers = await authHeaders(); // no Content-Type — browser sets multipart boundary
      const res = await fetch(`${API}/host/crm/customers/import`, { method: "POST", headers, body: fd });
      if (!res.ok) throw new Error();
      const result = await res.json();
      setImportResult(result);
      if (result.created > 0) {
        toast.success(`Imported ${result.created} customer${result.created === 1 ? "" : "s"}`);
        fetchData();
      } else {
        toast.error("No new customers were imported");
      }
    } catch { toast.error("Import failed — check your file format"); } finally { setImporting(false); }
  };

  const openEdit = (r) => {
    setEditForm({ name: r.name || "", email: r.email || "", phone: r.phone || "", city: r.city || "", status: r.status || "lead" });
    setEditRow(r);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingEdit(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const res = await fetch(`${API}/host/crm/customers/${editRow.id}`, {
        method: "PATCH", headers, body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error();
      setData((prev) => prev ? {
        ...prev,
        customers: prev.customers.map((c) => c.id === editRow.id ? { ...c, ...editForm } : c),
      } : prev);
      toast.success("Customer updated");
      setEditRow(null);
    } catch { toast.error("Update failed"); } finally { setSavingEdit(false); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/crm/customers/${deleteRow.id}`, { method: "DELETE", headers });
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || "This customer can't be deleted");
        setDeleteRow(null);
        return;
      }
      if (!res.ok) throw new Error();
      setData((prev) => prev ? {
        ...prev,
        customers: prev.customers.filter((c) => c.id !== deleteRow.id),
        summary: { ...prev.summary, total_customers: Math.max(0, (prev.summary?.total_customers || 1) - 1) },
      } : prev);
      toast.success("Customer deleted");
      setDeleteRow(null);
    } catch { toast.error("Delete failed"); } finally { setDeleting(false); }
  };

  return (
    <HostLayout>
      <div data-testid="host-customers-page">
        <HostPageHeader
          icon={Contact}
          title="Customers"
          subtitle="Your CRM of truth — every client across your gym, studio, classes and trips, in one place for the whole team."
          actions={
            <>
              <Button variant="outline" size="sm" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} className="gap-1.5" data-testid="customers-import-btn">
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5" data-testid="customers-add-btn">
                <UserPlus className="w-4 h-4" /> Add customer
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="customers-more-btn">
                    <MoreHorizontal className="w-4 h-4" /> More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportCsv} data-testid="customers-export-item">
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <KpiCard label="Total customers" value={summary?.total_customers ?? 0} icon={Users} />
              <KpiCard label="Active" value={summary?.active ?? 0} icon={Contact} accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" />
              <KpiCard label="Lapsed" value={summary?.lapsed ?? 0} icon={Contact} accent="bg-amber-500/15 text-amber-700 dark:text-amber-300" />
              <KpiCard label="Lifetime value" value={formatMoneyMap(summary?.ltv_by_currency)} icon={Users} accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" />
            </div>

            {/* Status segments */}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="customers-status-filters">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setStatusFilter(s.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    statusFilter === s.value ? "bg-foreground text-background" : "bg-muted/60 text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`customers-filter-${s.value}`}
                >
                  {s.label}
                  {s.value !== "all" && summary ? ` (${summary[s.value] ?? 0})` : ""}
                </button>
              ))}
            </div>

            {/* Tag chips */}
            {(data?.tags || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs text-muted-foreground">Tags:</span>
                {(data.tags).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTagFilter(tagFilter === t ? null : t)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                      tagFilter === t ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`customers-tag-${t}`}
                  >
                    #{t}
                  </button>
                ))}
              </div>
            )}

            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, email, phone or city…"
                className="pl-9"
                data-testid="customers-search"
              />
            </div>

            {rows.length === 0 ? (
              <Card className="p-10 text-center border-dashed" data-testid="customers-empty">
                <Contact className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium">{q || tagFilter || statusFilter !== "all" ? "No customers match your filters" : "No customers yet"}</p>
                <p className="text-xs text-muted-foreground mt-1">Once people book your events they land here automatically — or add one manually.</p>
              </Card>
            ) : (
              <Card className="overflow-hidden" data-testid="customers-list">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="font-medium px-4 py-3">Name</th>
                        <th className="font-medium px-4 py-3 hidden sm:table-cell">Email</th>
                        <th className="font-medium px-4 py-3 hidden md:table-cell">Phone</th>
                        <th className="font-medium px-4 py-3 hidden lg:table-cell">Tags</th>
                        <th className="font-medium px-4 py-3 hidden sm:table-cell">Last Interaction</th>
                        <th className="font-medium px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr
                          key={r.id || i}
                          onClick={() => navigate(`/host/customers/${r.id}`)}
                          className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                          data-testid={`customer-row-${i}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-9 h-9 shrink-0 ring-1 ring-border">
                                <AvatarImage src={r.picture} alt={r.name} />
                                <AvatarFallback className="bg-primary/20 text-primary-ink text-xs">{initials(r.name)}</AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">{r.name}</span>
                                  <StatusPill status={r.status} />
                                </div>
                                <span className="text-xs text-muted-foreground truncate sm:hidden">{r.email || r.phone || "—"}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell max-w-[220px]">
                            <span className="text-muted-foreground truncate block">{r.email || "—"}</span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{r.phone || "—"}</td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {(r.tags || []).length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {r.tags.slice(0, 3).map((t) => (
                                  <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">#{t}</span>
                                ))}
                                {r.tags.length > 3 && <span className="text-[10px] text-muted-foreground">+{r.tags.length - 3}</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{lastInteraction(r.last_at)}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`customer-actions-${i}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`customer-edit-${i}`}>
                                  <Pencil className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setDeleteRow(r)} className="text-destructive focus:text-destructive" data-testid={`customer-delete-${i}`}>
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Add customer dialog (shared with sidebar quick-add) */}
      <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={fetchData} />

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-customers-dialog">
          <DialogHeader>
            <DialogTitle>Bulk import customers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV with columns <span className="font-medium text-foreground">Name</span> (required), Email, Phone, City, Status, Tags.
              Rows with an email that already exists are skipped.
            </p>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 text-sm text-primary-ink hover:underline"
              data-testid="import-template-btn"
            >
              <FileText className="w-4 h-4" /> Download CSV template
            </button>

            <label
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg py-8 cursor-pointer hover:border-primary/50 transition-colors"
              data-testid="import-dropzone"
            >
              <Upload className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium">{importFile ? importFile.name : "Choose a CSV file"}</span>
              <span className="text-xs text-muted-foreground">.csv up to 5MB</span>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                data-testid="import-file-input"
              />
            </label>

            {importResult && (
              <div className="text-sm rounded-lg bg-muted/60 p-3 space-y-1" data-testid="import-result">
                <p><span className="font-semibold text-emerald-700 dark:text-emerald-300">{importResult.created}</span> added · <span className="font-semibold">{importResult.skipped}</span> skipped</p>
                {(importResult.errors || []).length > 0 && (
                  <ul className="text-xs text-muted-foreground list-disc pl-4 max-h-24 overflow-y-auto">
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button onClick={runImport} disabled={importing || !importFile} data-testid="import-run-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit customer dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Edit customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="e-name">Name *</Label>
              <Input id="e-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-customer-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="e-email">Email</Label>
                <Input id="e-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-customer-email" />
              </div>
              <div>
                <Label htmlFor="e-phone">Phone</Label>
                <Input id="e-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-customer-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="e-city">City</Label>
                <Input id="e-city" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} data-testid="edit-customer-city" />
              </div>
              <div>
                <Label>Lifecycle</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-customer-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-customer-save">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteRow?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the customer and their notes and tags. Customers with booking history can't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-customer-confirm">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HostLayout>
  );
};

export default HostCustomersPage;
